import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useLoading } from '../context/LoadingContext';

gsap.registerPlugin(ScrollTrigger);

const AnimatedCard = React.memo(({ children, className = '', delay = 0 }) => {
  const cardRef = useRef(null);
  const timelineRef = useRef(null);
  const isHoveringRef = useRef(false);
  const lastMouseTimeRef = useRef(0);
  const { isInitialized } = useLoading();

  // Memoize configuration for better performance
  const config = useMemo(() => ({
    rotationIntensity: 2, // Reduced from 5
    translateIntensity: 5, // Reduced from 10
    scaleHover: 1.01, // Reduced from 1.02
    throttleDelay: 16 // ~60fps throttling
  }), []);

  // Throttled mouse move handler
  const handleMouseMove = useCallback((e) => {
    const now = Date.now();
    if (now - lastMouseTimeRef.current < config.throttleDelay) return;
    lastMouseTimeRef.current = now;

    if (!cardRef.current || !isHoveringRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    const xPercent = (mouseX / rect.width - 0.5) * 2;
    const yPercent = (mouseY / rect.height - 0.5) * 2;
    
    // Simplified rotation with GPU acceleration
    gsap.to(cardRef.current, {
      rotationY: xPercent * config.rotationIntensity,
      rotationX: -yPercent * config.rotationIntensity,
      duration: 0.3,
      ease: "power2.out",
      force3D: true
    });

    // Simplified shadow effect
    gsap.to(cardRef.current, {
      boxShadow: `
        ${-xPercent * 10}px ${-yPercent * 10}px 15px rgba(0,0,0,0.08),
        0 5px 15px rgba(0,0,0,0.08)
      `,
      duration: 0.3
    });
  }, [config]);

  const handleMouseEnter = useCallback(() => {
    isHoveringRef.current = true;
    gsap.to(cardRef.current, {
      scale: config.scaleHover,
      duration: 0.2,
      ease: "power2.out",
      force3D: true
    });
  }, [config.scaleHover]);

  const handleMouseLeave = useCallback(() => {
    isHoveringRef.current = false;
    gsap.to(cardRef.current, {
      rotationY: 0,
      rotationX: 0,
      scale: 1,
      boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
      duration: 0.3,
      ease: "power2.out",
      force3D: true
    });
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    const card = cardRef.current;
    if (!card) return;

    // If card is already within the viewport at the moment the animation is
    // created (for example, when the user refreshes while scrolled near the
    // FAQ section), render it instantly instead of hiding it first.

      // Initial state with GPU acceleration
      gsap.set(card, {
        opacity: 0,
        y: 20, // Reduced from 50
        force3D: true,
        transformOrigin: "center center"
      });

      // Create entrance animation
      timelineRef.current = gsap.timeline({ 
        delay,
        scrollTrigger: {
          trigger: card,
          start: "top bottom-=50", // Reduced trigger area
          toggleActions: "play none none reverse",
          once: false // Allow replay
        }
      });
      
      timelineRef.current.to(card, {
        opacity: 1,
        y: 0,
        duration: 0.6, // Reduced duration
        ease: "power2.out"
      });

    // Add passive event listeners for better performance
    card.addEventListener("mousemove", handleMouseMove, { passive: true });
    card.addEventListener("mouseenter", handleMouseEnter, { passive: true });
    card.addEventListener("mouseleave", handleMouseLeave, { passive: true });

    return () => {
      // Cleanup
      if (card) {
        card.removeEventListener("mousemove", handleMouseMove);
        card.removeEventListener("mouseenter", handleMouseEnter);
        card.removeEventListener("mouseleave", handleMouseLeave);
      }
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === card) {
          trigger.kill();
        }
      });
    };
  }, [delay, isInitialized, handleMouseMove, handleMouseEnter, handleMouseLeave]);

  return (
    <div 
      ref={cardRef} 
      className={`transform-gpu ${className}`}
      style={{ 
        transformStyle: "preserve-3d",
        backfaceVisibility: "hidden",
        willChange: 'transform, opacity',
        transform: 'translate3d(0,0,0)' // GPU acceleration hint
      }}
    >
      <div 
        style={{ 
          transformStyle: "preserve-3d",
          position: "relative",
          zIndex: 1
        }}
      >
        {children}
      </div>
    </div>
  );
});

AnimatedCard.displayName = 'AnimatedCard';

export default AnimatedCard; 